/**
 * FIKO VIEW-DEPENDENT STREAMING LOD SYSTEM
 * =========================================
 * FAZA 4.2: CATIA seviyesi progressive mesh loading
 *
 * 100M+ triangle sahneleri icin view-dependent streaming
 *
 * Ozellikler:
 * - View-dependent tessellation: Kameraya gore detail seviyesi
 * - Network-aware streaming: Bandwidth'e gore adapte
 * - Priority queue: Screen-space importance siralamasi
 * - Memory pooling: Geometry buffer reuse
 * - Predictive loading: Camera motion prediction
 * - Out-of-core rendering: Disk'ten lazy loading
 * - Geomorphing: Smooth LOD transitions
 *
 * HEDEFLER:
 * - <50ms initial render (bounding box ile)
 * - <500MB RAM for 100M triangle scenes
 * - 60 FPS during streaming
 * - Zero visible LOD pops (geomorphing)
 */

import * as THREE from 'three';

// ============================================================================
// INTERFACES & TYPES
// ============================================================================

export interface ViewDependentConfig {
    /** Maximum memory budget in bytes */
    maxMemoryBytes: number;
    /** Maximum concurrent network requests */
    maxConcurrentRequests: number;
    /** LOD bias (positive = higher quality, negative = lower quality) */
    lodBias: number;
    /** Enable geomorphing transitions */
    enableGeomorphing: boolean;
    /** Geomorphing duration in seconds */
    geomorphDuration: number;
    /** Predictive loading lookahead in seconds */
    predictionLookahead: number;
    /** Minimum screen space size for loading (pixels) */
    minScreenSpaceSize: number;
    /** Enable network-aware streaming */
    networkAware: boolean;
    /** Target frame time in ms */
    targetFrameTime: number;
}

export interface StreamingMeshState {
    id: string;
    mesh: THREE.Mesh;
    baseGeometry: THREE.BufferGeometry;
    currentLOD: number;
    targetLOD: number;
    lodGeometries: (THREE.BufferGeometry | null)[];
    lodTriangleCounts: number[];
    boundingSphere: THREE.Sphere;
    screenSpaceSize: number;
    screenSpaceImportance: number;
    distanceToCamera: number;
    isLoading: boolean;
    loadingLOD: number;
    geomorphProgress: number;
    lastAccessTime: number;
    memoryBytes: number;
    priority: number;
}

export interface NetworkStats {
    estimatedBandwidthKbps: number;
    avgLatencyMs: number;
    pendingBytes: number;
    lastMeasureTime: number;
}

export interface PredictionState {
    cameraPosition: THREE.Vector3;
    cameraDirection: THREE.Vector3;
    velocity: THREE.Vector3;
    angularVelocity: THREE.Vector3;
    lastUpdateTime: number;
}

export interface ViewDependentStats {
    meshCount: number;
    loadedLODs: number;
    pendingLoads: number;
    memoryUsedMB: number;
    memoryBudgetMB: number;
    avgLoadTimeMs: number;
    networkBandwidthKbps: number;
    frameTimeMs: number;
    culledMeshes: number;
    visibleMeshes: number;
}

// ============================================================================
// LOD LEVEL DEFINITIONS
// ============================================================================

const LOD_LEVELS = [
    { level: 0, screenSizeThreshold: 500, triangleRatio: 1.0, name: 'Ultra' },
    { level: 1, screenSizeThreshold: 200, triangleRatio: 0.5, name: 'High' },
    { level: 2, screenSizeThreshold: 100, triangleRatio: 0.25, name: 'Medium' },
    { level: 3, screenSizeThreshold: 50, triangleRatio: 0.1, name: 'Low' },
    { level: 4, screenSizeThreshold: 20, triangleRatio: 0.02, name: 'VeryLow' },
    { level: 5, screenSizeThreshold: 0, triangleRatio: 0.005, name: 'Minimal' }
];

// ============================================================================
// PRIORITY QUEUE
// ============================================================================

class PriorityQueue<T> {
    private items: Array<{ item: T; priority: number }> = [];

    enqueue(item: T, priority: number): void {
        const entry = { item, priority };
        let added = false;

        for (let i = 0; i < this.items.length; i++) {
            if (priority > this.items[i].priority) {
                this.items.splice(i, 0, entry);
                added = true;
                break;
            }
        }

        if (!added) {
            this.items.push(entry);
        }
    }

    dequeue(): T | undefined {
        return this.items.shift()?.item;
    }

    peek(): T | undefined {
        return this.items[0]?.item;
    }

    updatePriority(item: T, newPriority: number): void {
        const index = this.items.findIndex(e => e.item === item);
        if (index !== -1) {
            this.items.splice(index, 1);
            this.enqueue(item, newPriority);
        }
    }

    remove(item: T): boolean {
        const index = this.items.findIndex(e => e.item === item);
        if (index !== -1) {
            this.items.splice(index, 1);
            return true;
        }
        return false;
    }

    get length(): number {
        return this.items.length;
    }

    clear(): void {
        this.items = [];
    }

    toArray(): T[] {
        return this.items.map(e => e.item);
    }
}

// ============================================================================
// GEOMETRY POOL
// ============================================================================

class GeometryPool {
    private pools: Map<number, THREE.BufferGeometry[]> = new Map();
    private maxPoolSize: number = 50;

    acquire(triangleCount: number): THREE.BufferGeometry | null {
        // Find pool for similar size
        const sizeKey = this.getSizeKey(triangleCount);
        const pool = this.pools.get(sizeKey);

        if (pool && pool.length > 0) {
            return pool.pop()!;
        }

        return null;
    }

    release(geometry: THREE.BufferGeometry): void {
        const triangleCount = geometry.index
            ? geometry.index.count / 3
            : geometry.getAttribute('position').count / 3;

        const sizeKey = this.getSizeKey(triangleCount);

        if (!this.pools.has(sizeKey)) {
            this.pools.set(sizeKey, []);
        }

        const pool = this.pools.get(sizeKey)!;
        if (pool.length < this.maxPoolSize) {
            // Clear data but keep structure
            pool.push(geometry);
        } else {
            geometry.dispose();
        }
    }

    private getSizeKey(triangleCount: number): number {
        // Bucket by power of 2
        return Math.pow(2, Math.ceil(Math.log2(Math.max(1, triangleCount))));
    }

    dispose(): void {
        for (const pool of this.pools.values()) {
            for (const geometry of pool) {
                geometry.dispose();
            }
        }
        this.pools.clear();
    }
}

// ============================================================================
// CAMERA MOTION PREDICTOR
// ============================================================================

class CameraMotionPredictor {
    private history: Array<{
        position: THREE.Vector3;
        direction: THREE.Vector3;
        time: number;
    }> = [];
    private maxHistoryLength: number = 10;

    private velocity: THREE.Vector3 = new THREE.Vector3();
    private angularVelocity: THREE.Vector3 = new THREE.Vector3();

    update(camera: THREE.Camera): void {
        const now = performance.now();
        const position = camera.position.clone();
        const direction = new THREE.Vector3(0, 0, -1).applyQuaternion(camera.quaternion);

        if (this.history.length > 0) {
            const last = this.history[this.history.length - 1];
            const dt = (now - last.time) / 1000;

            if (dt > 0) {
                // Calculate velocity
                this.velocity.subVectors(position, last.position).divideScalar(dt);

                // Calculate angular velocity (simplified)
                const angleDiff = direction.angleTo(last.direction);
                this.angularVelocity.set(0, angleDiff / dt, 0);
            }
        }

        this.history.push({ position, direction, time: now });

        // Trim history
        while (this.history.length > this.maxHistoryLength) {
            this.history.shift();
        }
    }

    predictPosition(lookahead: number): THREE.Vector3 {
        if (this.history.length === 0) {
            return new THREE.Vector3();
        }

        const last = this.history[this.history.length - 1];
        return last.position.clone().add(
            this.velocity.clone().multiplyScalar(lookahead)
        );
    }

    predictDirection(lookahead: number): THREE.Vector3 {
        if (this.history.length === 0) {
            return new THREE.Vector3(0, 0, -1);
        }

        const last = this.history[this.history.length - 1];
        // Simplified - just return current direction
        return last.direction.clone();
    }

    getVelocity(): THREE.Vector3 {
        return this.velocity.clone();
    }

    isMoving(): boolean {
        return this.velocity.lengthSq() > 0.01;
    }
}

// ============================================================================
// NETWORK BANDWIDTH ESTIMATOR
// ============================================================================

class BandwidthEstimator {
    private samples: Array<{ bytes: number; durationMs: number }> = [];
    private maxSamples: number = 20;
    private _estimatedKbps: number = 1000; // Default 1 Mbps

    addSample(bytes: number, durationMs: number): void {
        this.samples.push({ bytes, durationMs });

        while (this.samples.length > this.maxSamples) {
            this.samples.shift();
        }

        this.recalculate();
    }

    private recalculate(): void {
        if (this.samples.length === 0) return;

        let totalBytes = 0;
        let totalTime = 0;

        for (const sample of this.samples) {
            totalBytes += sample.bytes;
            totalTime += sample.durationMs;
        }

        if (totalTime > 0) {
            this._estimatedKbps = (totalBytes * 8) / totalTime; // kbps
        }
    }

    get estimatedKbps(): number {
        return this._estimatedKbps;
    }

    get estimatedMBps(): number {
        return this._estimatedKbps / 8000;
    }
}

// ============================================================================
// MAIN CLASS
// ============================================================================

export class FikoViewDependentLOD {
    private config: ViewDependentConfig;
    private camera: THREE.Camera;
    private renderer: THREE.WebGLRenderer;
    private scene: THREE.Scene;

    // Mesh states
    private meshStates: Map<string, StreamingMeshState> = new Map();

    // Loading queue
    private loadQueue: PriorityQueue<StreamingMeshState> = new PriorityQueue();
    private activeLoads: Set<string> = new Set();

    // Pools
    private geometryPool: GeometryPool = new GeometryPool();

    // Prediction
    private predictor: CameraMotionPredictor = new CameraMotionPredictor();
    private bandwidthEstimator: BandwidthEstimator = new BandwidthEstimator();

    // Memory tracking
    private memoryUsedBytes: number = 0;

    // Stats
    private stats: ViewDependentStats = {
        meshCount: 0,
        loadedLODs: 0,
        pendingLoads: 0,
        memoryUsedMB: 0,
        memoryBudgetMB: 0,
        avgLoadTimeMs: 0,
        networkBandwidthKbps: 0,
        frameTimeMs: 0,
        culledMeshes: 0,
        visibleMeshes: 0
    };

    // Frame timing
    private lastFrameTime: number = 0;
    private frameTimeAccum: number = 0;
    private frameCount: number = 0;

    // Pre-allocated
    private static readonly _tempVec3 = new THREE.Vector3();
    private static readonly _tempFrustum = new THREE.Frustum();
    private static readonly _tempMatrix = new THREE.Matrix4();

    constructor(
        camera: THREE.Camera,
        renderer: THREE.WebGLRenderer,
        scene: THREE.Scene,
        config?: Partial<ViewDependentConfig>
    ) {
        this.camera = camera;
        this.renderer = renderer;
        this.scene = scene;

        this.config = {
            maxMemoryBytes: config?.maxMemoryBytes ?? 512 * 1024 * 1024, // 512 MB
            maxConcurrentRequests: config?.maxConcurrentRequests ?? 4,
            lodBias: config?.lodBias ?? 0,
            enableGeomorphing: config?.enableGeomorphing ?? true,
            geomorphDuration: config?.geomorphDuration ?? 0.3,
            predictionLookahead: config?.predictionLookahead ?? 0.5,
            minScreenSpaceSize: config?.minScreenSpaceSize ?? 5,
            networkAware: config?.networkAware ?? true,
            targetFrameTime: config?.targetFrameTime ?? 16.67
        };

        this.stats.memoryBudgetMB = this.config.maxMemoryBytes / (1024 * 1024);
    }

    // ========================================================================
    // MESH MANAGEMENT
    // ========================================================================

    /**
     * Register a mesh for view-dependent LOD
     */
    registerMesh(
        id: string,
        mesh: THREE.Mesh,
        baseGeometry?: THREE.BufferGeometry
    ): void {
        const geometry = baseGeometry || mesh.geometry as THREE.BufferGeometry;

        // Compute bounding sphere
        if (!geometry.boundingSphere) {
            geometry.computeBoundingSphere();
        }

        // Create LOD geometries array
        const lodGeometries: (THREE.BufferGeometry | null)[] = new Array(LOD_LEVELS.length).fill(null);
        lodGeometries[0] = geometry; // Base LOD

        const lodTriangleCounts = LOD_LEVELS.map(lod => {
            const baseTriangles = geometry.index
                ? geometry.index.count / 3
                : geometry.getAttribute('position').count / 3;
            return Math.floor(baseTriangles * lod.triangleRatio);
        });

        const state: StreamingMeshState = {
            id,
            mesh,
            baseGeometry: geometry,
            currentLOD: 0,
            targetLOD: 0,
            lodGeometries,
            lodTriangleCounts,
            boundingSphere: geometry.boundingSphere!.clone(),
            screenSpaceSize: 0,
            screenSpaceImportance: 0,
            distanceToCamera: 0,
            isLoading: false,
            loadingLOD: -1,
            geomorphProgress: 1,
            lastAccessTime: performance.now(),
            memoryBytes: this.estimateMemory(geometry),
            priority: 0
        };

        this.meshStates.set(id, state);
        this.memoryUsedBytes += state.memoryBytes;
        this.stats.meshCount++;
    }

    /**
     * Unregister a mesh
     */
    unregisterMesh(id: string): void {
        const state = this.meshStates.get(id);
        if (!state) return;

        // Release LOD geometries
        for (let i = 1; i < state.lodGeometries.length; i++) {
            const geom = state.lodGeometries[i];
            if (geom) {
                this.geometryPool.release(geom);
                this.memoryUsedBytes -= this.estimateMemory(geom);
            }
        }

        this.loadQueue.remove(state);
        this.activeLoads.delete(id);
        this.meshStates.delete(id);
        this.stats.meshCount--;
    }

    // ========================================================================
    // UPDATE LOOP
    // ========================================================================

    /**
     * Main update - call every frame
     */
    update(deltaTime: number): void {
        const frameStart = performance.now();

        // Update predictor
        this.predictor.update(this.camera);

        // Update frustum for culling
        FikoViewDependentLOD._tempMatrix.multiplyMatrices(
            this.camera.projectionMatrix,
            this.camera.matrixWorldInverse
        );
        FikoViewDependentLOD._tempFrustum.setFromProjectionMatrix(
            FikoViewDependentLOD._tempMatrix
        );

        // Update all mesh states
        this.stats.culledMeshes = 0;
        this.stats.visibleMeshes = 0;

        for (const state of this.meshStates.values()) {
            this.updateMeshState(state, deltaTime);
        }

        // Process load queue
        this.processLoadQueue();

        // Memory management
        if (this.memoryUsedBytes > this.config.maxMemoryBytes * 0.9) {
            this.evictLODs();
        }

        // Update geomorphing
        if (this.config.enableGeomorphing) {
            this.updateGeomorphing(deltaTime);
        }

        // Update stats
        const frameTime = performance.now() - frameStart;
        this.frameTimeAccum += frameTime;
        this.frameCount++;

        if (this.frameCount >= 30) {
            this.stats.frameTimeMs = this.frameTimeAccum / this.frameCount;
            this.frameTimeAccum = 0;
            this.frameCount = 0;
        }

        this.stats.memoryUsedMB = this.memoryUsedBytes / (1024 * 1024);
        this.stats.pendingLoads = this.loadQueue.length;
        this.stats.networkBandwidthKbps = this.bandwidthEstimator.estimatedKbps;
    }

    /**
     * Update single mesh state
     */
    private updateMeshState(state: StreamingMeshState, deltaTime: number): void {
        // Transform bounding sphere to world space
        const worldCenter = state.boundingSphere.center.clone();
        state.mesh.localToWorld(worldCenter);

        // Check frustum culling
        const worldSphere = new THREE.Sphere(worldCenter, state.boundingSphere.radius * state.mesh.scale.x);

        if (!FikoViewDependentLOD._tempFrustum.intersectsSphere(worldSphere)) {
            state.mesh.visible = false;
            this.stats.culledMeshes++;
            return;
        }

        state.mesh.visible = true;
        this.stats.visibleMeshes++;

        // Calculate distance to camera
        state.distanceToCamera = worldCenter.distanceTo(this.camera.position);

        // Calculate screen space size
        const perspective = this.camera as THREE.PerspectiveCamera;
        if (perspective.fov) {
            const fovRad = (perspective.fov * Math.PI) / 180;
            const pixelHeight = this.renderer.domElement.height;
            state.screenSpaceSize = (worldSphere.radius * 2 * pixelHeight) /
                (state.distanceToCamera * 2 * Math.tan(fovRad / 2));
        }

        // Skip tiny objects
        if (state.screenSpaceSize < this.config.minScreenSpaceSize) {
            return;
        }

        // Calculate screen space importance (center distance + size)
        const screenCenter = worldCenter.clone().project(this.camera);
        const centerDist = Math.sqrt(screenCenter.x * screenCenter.x + screenCenter.y * screenCenter.y);
        state.screenSpaceImportance = state.screenSpaceSize * (1 - centerDist * 0.5);

        // Determine target LOD
        const targetLOD = this.calculateTargetLOD(state);
        state.targetLOD = targetLOD;

        // Calculate priority
        state.priority = this.calculatePriority(state);

        // Request LOD if needed
        if (state.targetLOD !== state.currentLOD && !state.isLoading) {
            this.requestLOD(state, state.targetLOD);
        }

        // Update access time
        state.lastAccessTime = performance.now();
    }

    /**
     * Calculate target LOD based on screen space size
     */
    private calculateTargetLOD(state: StreamingMeshState): number {
        const adjustedSize = state.screenSpaceSize + this.config.lodBias * 10;

        // Reduce quality if camera is moving fast
        let motionPenalty = 0;
        if (this.predictor.isMoving()) {
            motionPenalty = 1;
        }

        // Reduce quality if over memory budget
        let memoryPenalty = 0;
        if (this.memoryUsedBytes > this.config.maxMemoryBytes * 0.8) {
            memoryPenalty = 1;
        }

        const effectiveSize = adjustedSize / (1 + motionPenalty * 0.3 + memoryPenalty * 0.2);

        for (let i = 0; i < LOD_LEVELS.length; i++) {
            if (effectiveSize >= LOD_LEVELS[i].screenSizeThreshold) {
                return i;
            }
        }

        return LOD_LEVELS.length - 1;
    }

    /**
     * Calculate loading priority
     */
    private calculatePriority(state: StreamingMeshState): number {
        let priority = 0;

        // Screen space importance (0-100)
        priority += state.screenSpaceImportance;

        // LOD urgency (higher if big difference)
        const lodDiff = Math.abs(state.currentLOD - state.targetLOD);
        priority += lodDiff * 50;

        // Prefer higher quality (lower LOD numbers)
        priority += (LOD_LEVELS.length - state.targetLOD) * 10;

        // Predictive bonus
        if (this.config.predictionLookahead > 0) {
            const predictedPos = this.predictor.predictPosition(this.config.predictionLookahead);
            const predictedDist = state.boundingSphere.center.distanceTo(predictedPos);
            if (predictedDist < state.distanceToCamera) {
                priority += 30; // Will be closer, load proactively
            }
        }

        return priority;
    }

    // ========================================================================
    // LOD LOADING
    // ========================================================================

    /**
     * Request LOD loading
     */
    private requestLOD(state: StreamingMeshState, lodLevel: number): void {
        // Check if already loaded
        if (state.lodGeometries[lodLevel]) {
            this.applyLOD(state, lodLevel);
            return;
        }

        // Add to queue
        state.loadingLOD = lodLevel;
        this.loadQueue.enqueue(state, state.priority);
    }

    /**
     * Process load queue
     */
    private processLoadQueue(): void {
        while (
            this.activeLoads.size < this.config.maxConcurrentRequests &&
            this.loadQueue.length > 0
        ) {
            const state = this.loadQueue.dequeue();
            if (!state) break;

            // Skip if no longer needed
            if (state.loadingLOD !== state.targetLOD) {
                state.loadingLOD = -1;
                continue;
            }

            this.loadLOD(state, state.loadingLOD);
        }
    }

    /**
     * Load LOD geometry
     */
    private async loadLOD(state: StreamingMeshState, lodLevel: number): Promise<void> {
        state.isLoading = true;
        this.activeLoads.add(state.id);

        const startTime = performance.now();

        try {
            // Generate decimated geometry
            const targetRatio = LOD_LEVELS[lodLevel].triangleRatio;
            const geometry = await this.generateLODGeometry(state.baseGeometry, targetRatio);

            const loadTime = performance.now() - startTime;

            // Track bandwidth
            const bytes = this.estimateMemory(geometry);
            this.bandwidthEstimator.addSample(bytes, loadTime);

            // Store geometry
            state.lodGeometries[lodLevel] = geometry;
            this.memoryUsedBytes += bytes;
            this.stats.loadedLODs++;

            // Apply if still target
            if (state.targetLOD === lodLevel) {
                this.applyLOD(state, lodLevel);
            }

        } catch (e) {
            console.warn(`[ViewDependentLOD] Failed to load LOD ${lodLevel} for ${state.id}:`, e);
        }

        state.isLoading = false;
        state.loadingLOD = -1;
        this.activeLoads.delete(state.id);
    }

    /**
     * Generate decimated geometry
     */
    private async generateLODGeometry(
        baseGeometry: THREE.BufferGeometry,
        targetRatio: number
    ): Promise<THREE.BufferGeometry> {
        if (targetRatio >= 1) {
            return baseGeometry.clone();
        }

        // Try to get from pool
        const targetTriangles = Math.floor(
            (baseGeometry.index ? baseGeometry.index.count / 3 :
                baseGeometry.getAttribute('position').count / 3) * targetRatio
        );

        const pooled = this.geometryPool.acquire(targetTriangles);
        if (pooled) {
            // Would need to repopulate with new data
            // For now, just create new
        }

        // Yield to allow frame updates
        await new Promise(resolve => setTimeout(resolve, 0));

        // Simple vertex clustering decimation
        return this.decimateGeometry(baseGeometry, targetRatio);
    }

    /**
     * Decimate geometry using vertex clustering
     */
    private decimateGeometry(geometry: THREE.BufferGeometry, ratio: number): THREE.BufferGeometry {
        const position = geometry.getAttribute('position');
        const originalCount = position.count;
        const targetCount = Math.max(3, Math.floor(originalCount * ratio));

        // Compute bounds
        geometry.computeBoundingBox();
        const bbox = geometry.boundingBox!;
        const size = new THREE.Vector3();
        bbox.getSize(size);

        // Grid resolution
        const gridRes = Math.ceil(Math.cbrt(targetCount));
        const cellSize = new THREE.Vector3(
            size.x / gridRes,
            size.y / gridRes,
            size.z / gridRes
        );

        // Cluster vertices
        const clusters = new Map<string, { sum: THREE.Vector3; count: number }>();

        for (let i = 0; i < originalCount; i++) {
            const x = position.getX(i);
            const y = position.getY(i);
            const z = position.getZ(i);

            const cx = Math.floor((x - bbox.min.x) / cellSize.x);
            const cy = Math.floor((y - bbox.min.y) / cellSize.y);
            const cz = Math.floor((z - bbox.min.z) / cellSize.z);
            const key = `${cx},${cy},${cz}`;

            if (!clusters.has(key)) {
                clusters.set(key, { sum: new THREE.Vector3(), count: 0 });
            }

            const cluster = clusters.get(key)!;
            cluster.sum.x += x;
            cluster.sum.y += y;
            cluster.sum.z += z;
            cluster.count++;
        }

        // Create new positions
        const newPositions: number[] = [];
        const vertexMap = new Map<string, number>();
        let idx = 0;

        for (const [key, cluster] of clusters) {
            newPositions.push(
                cluster.sum.x / cluster.count,
                cluster.sum.y / cluster.count,
                cluster.sum.z / cluster.count
            );
            vertexMap.set(key, idx++);
        }

        // Rebuild indices
        const indices = geometry.getIndex();
        const newIndices: number[] = [];

        if (indices) {
            for (let i = 0; i < indices.count; i += 3) {
                const keys = [];
                for (let j = 0; j < 3; j++) {
                    const vi = indices.getX(i + j);
                    const x = position.getX(vi);
                    const y = position.getY(vi);
                    const z = position.getZ(vi);

                    const cx = Math.floor((x - bbox.min.x) / cellSize.x);
                    const cy = Math.floor((y - bbox.min.y) / cellSize.y);
                    const cz = Math.floor((z - bbox.min.z) / cellSize.z);
                    keys.push(`${cx},${cy},${cz}`);
                }

                const a = vertexMap.get(keys[0])!;
                const b = vertexMap.get(keys[1])!;
                const c = vertexMap.get(keys[2])!;

                // Skip degenerate
                if (a !== b && b !== c && a !== c) {
                    newIndices.push(a, b, c);
                }
            }
        }

        // Create geometry
        const decimated = new THREE.BufferGeometry();
        decimated.setAttribute('position', new THREE.Float32BufferAttribute(newPositions, 3));

        if (newIndices.length > 0) {
            decimated.setIndex(newIndices);
        }

        decimated.computeVertexNormals();
        decimated.computeBoundingSphere();

        return decimated;
    }

    /**
     * Apply LOD to mesh
     */
    private applyLOD(state: StreamingMeshState, lodLevel: number): void {
        const geometry = state.lodGeometries[lodLevel];
        if (!geometry) return;

        if (this.config.enableGeomorphing) {
            // Start geomorphing
            state.geomorphProgress = 0;
            state.currentLOD = lodLevel;
        } else {
            state.mesh.geometry = geometry;
            state.currentLOD = lodLevel;
        }
    }

    // ========================================================================
    // GEOMORPHING
    // ========================================================================

    /**
     * Update geomorphing transitions
     */
    private updateGeomorphing(deltaTime: number): void {
        for (const state of this.meshStates.values()) {
            if (state.geomorphProgress < 1) {
                state.geomorphProgress += deltaTime / this.config.geomorphDuration;

                if (state.geomorphProgress >= 1) {
                    state.geomorphProgress = 1;
                    // Apply final geometry
                    const geometry = state.lodGeometries[state.currentLOD];
                    if (geometry) {
                        state.mesh.geometry = geometry;
                    }
                } else {
                    // During transition, could blend between LODs
                    // For simplicity, just use current geometry
                    const geometry = state.lodGeometries[state.currentLOD];
                    if (geometry) {
                        state.mesh.geometry = geometry;
                    }
                }
            }
        }
    }

    // ========================================================================
    // MEMORY MANAGEMENT
    // ========================================================================

    /**
     * Evict unused LODs to free memory
     */
    private evictLODs(): void {
        const now = performance.now();
        const evictionThreshold = 10000; // 10 seconds

        // Collect eviction candidates
        const candidates: Array<{
            state: StreamingMeshState;
            lodLevel: number;
            lastUsed: number;
            bytes: number;
        }> = [];

        for (const state of this.meshStates.values()) {
            for (let i = 1; i < state.lodGeometries.length; i++) {
                const geom = state.lodGeometries[i];
                if (geom && i !== state.currentLOD && i !== state.targetLOD) {
                    candidates.push({
                        state,
                        lodLevel: i,
                        lastUsed: state.lastAccessTime,
                        bytes: this.estimateMemory(geom)
                    });
                }
            }
        }

        // Sort by last used
        candidates.sort((a, b) => a.lastUsed - b.lastUsed);

        // Evict until under budget
        for (const candidate of candidates) {
            if (this.memoryUsedBytes <= this.config.maxMemoryBytes * 0.7) {
                break;
            }

            const geom = candidate.state.lodGeometries[candidate.lodLevel];
            if (geom) {
                this.geometryPool.release(geom);
                candidate.state.lodGeometries[candidate.lodLevel] = null;
                this.memoryUsedBytes -= candidate.bytes;
                this.stats.loadedLODs--;
            }
        }
    }

    /**
     * Estimate geometry memory usage
     */
    private estimateMemory(geometry: THREE.BufferGeometry): number {
        let bytes = 0;

        for (const name in geometry.attributes) {
            const attr = geometry.getAttribute(name);
            bytes += attr.array.byteLength;
        }

        if (geometry.index) {
            bytes += geometry.index.array.byteLength;
        }

        return bytes;
    }

    // ========================================================================
    // PUBLIC API
    // ========================================================================

    /**
     * Get statistics
     */
    getStats(): ViewDependentStats {
        return { ...this.stats };
    }

    /**
     * Set LOD bias
     */
    setLODBias(bias: number): void {
        this.config.lodBias = bias;
    }

    /**
     * Set memory budget
     */
    setMemoryBudget(bytes: number): void {
        this.config.maxMemoryBytes = bytes;
        this.stats.memoryBudgetMB = bytes / (1024 * 1024);
    }

    /**
     * Force update all LODs (for quality change)
     */
    forceUpdateAll(): void {
        for (const state of this.meshStates.values()) {
            state.targetLOD = this.calculateTargetLOD(state);
            if (state.targetLOD !== state.currentLOD) {
                this.requestLOD(state, state.targetLOD);
            }
        }
    }

    /**
     * Dispose all resources
     */
    dispose(): void {
        for (const [id] of this.meshStates) {
            this.unregisterMesh(id);
        }

        this.loadQueue.clear();
        this.geometryPool.dispose();
        this.meshStates.clear();
    }
}

// ============================================================================
// SINGLETON & FACTORY
// ============================================================================

let _viewDependentLODInstance: FikoViewDependentLOD | null = null;

/**
 * Get or create view-dependent LOD instance
 */
export function getViewDependentLOD(
    camera?: THREE.Camera,
    renderer?: THREE.WebGLRenderer,
    scene?: THREE.Scene,
    config?: Partial<ViewDependentConfig>
): FikoViewDependentLOD | null {
    if (!_viewDependentLODInstance && camera && renderer && scene) {
        _viewDependentLODInstance = new FikoViewDependentLOD(camera, renderer, scene, config);
    }
    return _viewDependentLODInstance;
}

/**
 * Dispose singleton instance
 */
export function disposeViewDependentLOD(): void {
    if (_viewDependentLODInstance) {
        _viewDependentLODInstance.dispose();
        _viewDependentLODInstance = null;
    }
}
